import { z } from "zod";
import type { Role } from "./types.js";

// =====================================================================
// Autocadastro por domínio + política de senha.
//
// LIMITE CONHECIDO E ACEITO: liberar o cadastro por domínio NÃO prova
// posse da caixa. Quem digitar "qualquer.coisa@brasilnovo.com" entra —
// não há confirmação por e-mail. A mitigação é o PAPEL:
//
//   quem se autocadastra nasce em SIGNUP_ROLE = "motorista" (somente
//   leitura). Nenhuma conta criada por essa porta consegue escrever na
//   operação; promover continua sendo ação de admin em /admin.
//
// Enquanto não houver confirmação por e-mail, esse teto é o que separa
// "cadastro aberto para a equipe" de "porta de escrita para qualquer um".
// Ver docs/DECISIONS.md D-22.
// =====================================================================

/** Papel de quem entra pela porta do autocadastro. Somente leitura. */
export const SIGNUP_ROLE: Role = "motorista";

/** Domínios aceitos quando o ambiente não define outros (SIGNUP_ALLOWED_DOMAINS). */
export const DEFAULT_SIGNUP_DOMAINS = ["brasilnovo.com"] as const;

/** Domínio de um e-mail, em caixa baixa. `null` se não houver exatamente um "@". */
export function emailDomain(email: unknown): string | null {
  const partes = String(email ?? "").trim().toLowerCase().split("@");
  if (partes.length !== 2) return null;
  const [local, dominio] = partes;
  if (!local || !dominio || !dominio.includes(".")) return null;
  return dominio;
}

/**
 * O e-mail pertence a um domínio liberado para autocadastro?
 *
 * Subdomínio NÃO é aceito por herança: "brasilnovo.com.br.evil.com" termina
 * com um sufixo que parece nosso e passaria numa checagem por `endsWith`.
 * A comparação é de igualdade exata do domínio.
 */
export function isAllowedSignupDomain(
  email: unknown,
  dominiosPermitidos: readonly string[] = DEFAULT_SIGNUP_DOMAINS,
): boolean {
  const d = emailDomain(email);
  if (!d) return false;
  return dominiosPermitidos.some((permitido) => permitido.trim().toLowerCase() === d);
}

/** Lê a lista de domínios de uma variável de ambiente ("a.com, b.com"). */
export function parseSignupDomains(bruto: string | undefined): string[] {
  const lista = (bruto ?? "")
    .split(/[,;\s]+/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  return lista.length ? lista : [...DEFAULT_SIGNUP_DOMAINS];
}

// ---------------------------------------------------------------------
// Política de senha
//
// Comprimento é o fator que mais pesa; a variedade de classes é um piso
// contra "brasilnovo" e "12345678". Exigir as QUATRO classes empurra a
// pessoa para o padrão "Senha1!" — curto e previsível — então pedimos 3 de 4.
// ---------------------------------------------------------------------
export const PASSWORD_MIN = 10;

export interface PasswordCheck {
  ok: boolean;
  /** Problemas em linguagem de usuário, prontos para exibir. */
  problemas: string[];
}

export function checkPassword(senha: unknown): PasswordCheck {
  const s = String(senha ?? "");
  const problemas: string[] = [];

  if (s.length < PASSWORD_MIN) problemas.push(`Use ao menos ${PASSWORD_MIN} caracteres.`);

  const classes = [
    { re: /[a-z]/, nome: "letra minúscula" },
    { re: /[A-Z]/, nome: "letra maiúscula" },
    { re: /[0-9]/, nome: "número" },
    { re: /[^A-Za-z0-9]/, nome: "símbolo" },
  ];
  const presentes = classes.filter((c) => c.re.test(s));
  if (presentes.length < 3) {
    const faltando = classes.filter((c) => !c.re.test(s)).map((c) => c.nome);
    problemas.push(`Combine ao menos 3 tipos de caractere — faltam: ${faltando.join(", ")}.`);
  }

  // NOTA: não há bloqueio do nome da empresa na senha. Seria uma regra útil,
  // mas a senha de provisionamento pedida para a conta de teste é
  // "Brasilnovo2026!" — uma política que rejeitasse o próprio credencial que
  // o sistema entrega seria incoerente. Como toda senha provisionada é
  // trocada no primeiro acesso, ela não sobrevive de qualquer forma.

  return { ok: problemas.length === 0, problemas };
}

export const senhaZod = z.string().superRefine((valor, ctx) => {
  for (const problema of checkPassword(valor).problemas) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: problema });
  }
});

export const registerSchema = z.object({
  email: z.string().email("Informe um e-mail válido"),
  name: z.string().trim().min(3, "Informe seu nome completo"),
  password: senhaZod,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Informe a senha atual"),
    newPassword: senhaZod,
  })
  .refine((d) => d.currentPassword !== d.newPassword, {
    message: "A nova senha precisa ser diferente da atual",
    path: ["newPassword"],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
